import React, { useContext } from 'react';
import styled from 'styled-components';
import { Edit } from '../../../../icons/Edit';
import { Primary } from '../../../../icons/Primary';
import { Modal, ModalContents, ModalOpenButton } from '../../../modal';
import { EditAddress } from '../address-modal/EditAddress';
import { AddressSelectionContext } from '../../AddressSelectionProvider';
import { colors } from '../../../../theme/color';
import {
  UpdateUserAddressRequest,
  UserAddressDto,
} from '@kotipizzagroup/kotipizza-users-api-client';

type AddressProps = {
  address: UserAddressDto;
  onAddressSelect: (addressId: number | undefined) => void;
  updateAddress: (address: UpdateUserAddressRequest) => Promise<void>;
  deleteAddress: (address: UserAddressDto) => Promise<void>;
  setDefaultAddress: (address: UserAddressDto) => Promise<void>;
};

export const AddressBookRow: React.FC<AddressProps> = ({
  address,
  onAddressSelect,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
}) => {
  const { addressBookAddressId, inputAddress } = useContext(AddressSelectionContext);
  const { addressId, streetAddress, zipCode, city, extraInfo, apartmentNumber } = address;

  const isAddressSelected = (): boolean => {
    return (
      addressId === addressBookAddressId ||
      `${streetAddress}${city}` === `${inputAddress?.streetAddress}${inputAddress?.city}`
    );
  };

  return (
    <AddressBookRowContainer data-clarity-mask="true">
      <TapTarget onClick={() => onAddressSelect(addressId)}>
        <div
          style={{
            visibility: isAddressSelected() ? 'visible' : 'hidden',
          }}
        >
          <PrimaryIcon color={colors.brandPrimary} />
        </div>
      </TapTarget>
      <MainColumn onClick={() => onAddressSelect(addressId)}>
        <WrapperItem>
          <StreetAddressContainer>
            <BoldSpan>
              <span translate="no">
                {' '}
                {streetAddress} {apartmentNumber}
              </span>
            </BoldSpan>
            <UpperCaseSpan>
              <span translate="no">
                {zipCode} {city}
              </span>
            </UpperCaseSpan>
          </StreetAddressContainer>
        </WrapperItem>
        <WrapperItem>
          <TextElipsis>{extraInfo}</TextElipsis>
        </WrapperItem>
      </MainColumn>
      <div>
        <Modal name={`editAddressModal-${address.addressId}`}>
          <StyledModalOpenButton
            noStyle
            aria-label="Muokkaa osoitetta"
            data-testid="editAddressButton"
          >
            <EditTapTarget>
              <EditIcon color={colors.brandPrimary} />
            </EditTapTarget>
          </StyledModalOpenButton>
          <ModalContents
            maxWidth="sm"
            fullWidth={true}
            title="Muokkaa kuljetusosoitetta"
            aria-labelledby="edit-address-modal"
            aria-describedby="edit-address-modal"
            data-testid="edit-address-modal"
          >
            <EditAddress
              address={address}
              updateAddress={updateAddress}
              deleteAddress={deleteAddress}
              setDefaultAddress={setDefaultAddress}
            />
          </ModalContents>
        </Modal>
      </div>
    </AddressBookRowContainer>
  );
};

export default React.memo(AddressBookRow);

const StyledModalOpenButton = styled(ModalOpenButton)`
  &:focus-visible {
    & > div {
      border-radius: 4px;
      outline: 2px solid ${colors.outlineDefault};
    }
  }
`;

const EditIcon = styled(Edit)`
  width: 1.43em;
  height: 1.43em;
`;

const PrimaryIcon = styled(Primary)`
  width: 1.43em;
  height: 1.43em;
`;

const AddressBookRowContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  padding: 10px 0;
  border-top: 1px solid ${colors.borderDefault};
  border-left: 1px solid ${colors.borderDefault};
  border-right: 1px solid ${colors.borderDefault};

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom: 1px solid ${colors.borderDefault};
    border-bottom-left-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
    border-bottom-right-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
  }

  .edit {
    cursor: pointer;
  }

  &:hover,
  &:focus,
  &:focus-within {
    background: ${colors.green.lighter(90)};
  }
`;

const TapTarget = styled.div`
  cursor: pointer;
  margin-left: 10px;
`;

const EditTapTarget = styled(TapTarget)`
  width: 40px;
  height: 40px;
  svg {
    margin: 0 auto;
  }

  &:hover {
    svg {
      color: ${colors.green.darker(30)};
    }
  }
`;

const MainColumn = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0 15px;
  flex-grow: 4;
  text-align: left;
  margin-left: 0;
  border-radius: 4px;

  &:focus-visible {
    outline: 2px solid ${colors.outlineDefault};
    outline-offset: -2px;
  }
`;

const StreetAddressContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: black;
`;

const WrapperItem = styled.div`
  display: flex;
  flex-wrap: nowrap;
`;

const TextElipsis = styled.span`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 0.875rem;
  color: ${colors.gray60};
`;

const BoldSpan = styled.span`
  font-weight: bold;
  line-height: 1.25rem;
`;

const UpperCaseSpan = styled(BoldSpan)`
  text-transform: uppercase;
`;
